import { onMounted, onBeforeUnmount, watch, isRef } from 'vue'

/**
 * 요소에 좌우 스와이프 제스처를 붙여 이전/다음 네비게이션을 호출한다.
 * (DayAgendaSheet의 터치 로직을 공용화)
 *
 * @param {Ref<HTMLElement>} elRef - 대상 요소 ref
 * @param {{ onPrev?: Function, onNext?: Function, threshold?: number }} options
 */
export function useSwipeNavigation(elRef, { onPrev, onNext, threshold = 50 } = {}) {
  let startX = 0
  let startY = 0
  let tracking = false
  let attached = null
  // 손가락이 가로 스크롤 상자 안에서 시작했을 때 그 상자와 시작 시점 scrollLeft
  let scroller = null
  let scrollerLeft = 0

  /** target 에서 루트(attached)까지 올라가며 가로로 스크롤되는 상자를 찾는다 */
  function horizontalScrollerOf(target) {
    for (let n = target; n && n !== attached; n = n.parentElement) {
      if (n.scrollWidth > n.clientWidth + 1) {
        const ox = getComputedStyle(n).overflowX
        if (ox === 'auto' || ox === 'scroll') return n
      }
    }
    return null
  }

  function handleStart(e) {
    if (!e.touches || e.touches.length !== 1) return
    startX = e.touches[0].clientX
    startY = e.touches[0].clientY
    scroller = horizontalScrollerOf(e.target)
    scrollerLeft = scroller ? scroller.scrollLeft : 0
    tracking = true
  }

  function handleEnd(e) {
    if (!tracking) return
    tracking = false
    const t = (e.changedTouches && e.changedTouches[0])
    if (!t) return
    /*
     * 가로 스크롤 상자를 밀어서 실제로 스크롤됐다면 그건 스와이프가 아니다.
     * 태블릿의 주 뷰는 1,110px 이라 그리드를 옆으로 밀어 봐야 하는데, 예전에는
     * 그 손짓이 끝날 때마다 주가 통째로 넘어갔다.
     */
    if (scroller && scroller.scrollLeft !== scrollerLeft) return
    const dx = t.clientX - startX
    const dy = t.clientY - startY
    // 가로 이동이 충분하고, 세로 이동보다 우세할 때만 동작
    if (Math.abs(dx) > threshold && Math.abs(dx) > Math.abs(dy) * 1.5) {
      if (dx > 0) {
        if (onPrev) onPrev()
      } else if (onNext) {
        onNext()
      }
    }
  }

  function attach(el) {
    if (!el || attached === el) return
    detach()
    el.addEventListener('touchstart', handleStart, { passive: true })
    el.addEventListener('touchend', handleEnd, { passive: true })
    attached = el
  }

  function detach() {
    if (attached) {
      attached.removeEventListener('touchstart', handleStart)
      attached.removeEventListener('touchend', handleEnd)
      attached = null
    }
  }

  onMounted(() => {
    attach(isRef(elRef) ? elRef.value : elRef)
  })

  if (isRef(elRef)) {
    watch(elRef, (el) => attach(el))
  }

  onBeforeUnmount(detach)

  return { detach }
}
